import { VERSAO_ATUAL } from './versao';
import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import { useEffect } from "react";

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]']);

export function isLocalHost(hostname: string) {
  return LOCAL_HOSTS.has(hostname);
}

export function initSentry() {
  const isEnabled = import.meta.env.VITE_ENABLE_SENTRY === 'true';
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!isEnabled || !dsn) {
    console.log('Sentry disabled or DSN not configured');
    return;
  }

  // A local production build carries the real DSN and would report as production.
  if (isLocalHost(window.location.hostname)) {
    console.log('Sentry disabled on a local host');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
    release: import.meta.env.VITE_SENTRY_RELEASE || 'unknown',
    // Enquanto as duas versões convivem, os erros vêm separados por versão.
    initialScope: { tags: { versao: VERSAO_ATUAL } },
    
    // Integrations
    integrations: [
      // React Router integration (the v7 one also covers react-router 8)
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Replay para gravar sessões com erros
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      // HTTP breadcrumbs
      Sentry.browserTracingIntegration(),
    ],

    // Performance Monitoring
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
    
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% das sessões
    replaysOnErrorSampleRate: 1.0, // 100% quando há erro

    // Filtrar dados sensíveis
    beforeSend(event, hint) {
      // Erro de rede com o browser offline = ruído esperado (não é bug nosso).
      // Defesa adicional ao shouldReport() para erros que chegam por outros
      // caminhos (unhandled rejection, ErrorBoundary).
      const exc = hint?.originalException;
      const msg =
        exc instanceof Error ? exc.message : typeof exc === "string" ? exc : "";
      const isNetwork = /failed to fetch|network\s?error/i.test(msg);
      if (
        isNetwork &&
        typeof navigator !== "undefined" &&
        navigator.onLine === false
      ) {
        return null;
      }

      // Remove tokens e senhas dos dados
      if (event.request?.data) {
        const data = event.request.data;
        if (typeof data === 'object') {
          const sensitive = data as Record<string, unknown>;
          delete sensitive.password;
          delete sensitive.token;
          delete sensitive.apikey;
        }
      }

      // Remove headers sensíveis
      if (event.request?.headers) {
        delete event.request.headers.Authorization;
        delete event.request.headers.authorization;
        delete event.request.headers.apikey;
      }

      return event;
    },

    // Ignorar erros comuns que não são bugs
    ignoreErrors: [
      // Erros de extensões de navegador
      'Non-Error promise rejection captured',
      'ResizeObserver loop limit exceeded',
      // Erros de rede que já são tratados
      /NetworkError/i,
    ],
  });

  // Definir contexto global
  Sentry.setTag('app', 'corisco-frilas-frontend');
}

// Helper para capturar erros manualmente
export function captureError(error: Error, context?: Record<string, any>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

// Helper para adicionar breadcrumb
export function addBreadcrumb(message: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    level: 'info',
    data,
  });
}

// Helper para definir contexto do usuário
export function setSentryUser(user: { id: string; email?: string; username?: string }) {
  Sentry.setUser(user);
}

// Helper para limpar contexto do usuário (logout)
export function clearSentryUser() {
  Sentry.setUser(null);
}
